import { ColumnDef } from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import {
  Content,
  getFormattedContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext/EventMapContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import * as styles from 'src/tables/SponsoredListings/SponsoredListingsTable.css';
import { TableShimmeringDiv } from 'src/tables/Table';
import { MOCK_SPONSORED_LISTINGS_DATA } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  applyNumberFormatting,
  formatPercent,
} from 'src/utils/numberFormatter';
import {
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
  TicketClassInfo,
} from 'src/WebApiController';

export const getTableColumnDefinitions = (): ColumnDef<
  [SponsoredListingsCampaign, SponsoredListingsFlight]
>[] => [
  {
    id: 'flight',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.CampaignName} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[1].flightId,
    cell: function Cell({ getValue, row: { original } }) {
      const contentContext = useContentContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue() as number;
      const flightsSorted = (original[0].flights ?? []).toSorted(
        (a, b) =>
          new Date(a.dateRange.start ?? 0).getTime() -
          new Date(b.dateRange.start ?? 0).getTime()
      );
      const flightNumberText = getFormattedContent(
        FormatContentId.FlightNumberX,
        [`${flightsSorted.findIndex((f) => f.flightId === value) + 1}`],
        contentContext
      );
      return (
        <BaseCell align="left" style={{ textTransform: 'none' }}>
          {original[0].campaignName} - {flightNumberText}
        </BaseCell>
      );
    },
  },
  {
    id: 'startDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.StartDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[1].dateRange?.start,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue() as string;
      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'endDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.EndDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[1].dateRange?.end,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue() as string;
      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'revenue',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Revenue} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      // Find the campaign that contains this flight
      const campaignWithFlight = Object.values(campaignSalesAggregates).find(
        (campaign) =>
          campaign.flightsAggregate &&
          campaign.flightsAggregate[original[1].flightId]
      );

      const flightData =
        campaignWithFlight?.flightsAggregate?.[original[1].flightId];

      const currency = getUiCurrency(
        flightData?.currencyCode || original?.[0].currencyCode || 'USD'
      );

      let revenue = flightData?.totalNetProceeds;

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $100 and $10,000
        revenue = Math.floor(Math.random() * 9900) + 100;
      }

      return (
        <BaseCell align="right" style={{ padding: vars.spacing.xs }}>
          {revenue && (
            <div
              className={revenue > 0 ? styles.revenuePositiveCell : undefined}
            >
              {applyNumberFormatting({
                inputNumber: revenue,
                currencyCode: currency.code,
                currencyDecimalDigits: currency.dec,
              })}
            </div>
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'flightSpend',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.AdSpend} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[1].flightSpend,
    cell: function Cell({ getValue, row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const currency = getUiCurrency(original[0].currencyCode || 'USD');
      const spend = getValue() as number | null;

      return (
        <BaseCell align="right">
          {spend &&
            applyNumberFormatting({
              inputNumber: spend,
              currencyCode: currency.code,
              currencyDecimalDigits: currency.dec,
            })}
        </BaseCell>
      );
    },
  },
  {
    id: 'roas',
    header: () => <TableHeaderCellDiv align="right">ROAS</TableHeaderCellDiv>,
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      // Find the campaign that contains this flight
      const campaignWithFlight = Object.values(campaignSalesAggregates).find(
        (campaign) =>
          campaign.flightsAggregate &&
          campaign.flightsAggregate[original[1].flightId]
      );

      const flightData =
        campaignWithFlight?.flightsAggregate?.[original[1].flightId];

      let revenue = flightData?.totalNetProceeds;

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $1 and budget * 10
        revenue =
          Math.floor(
            Math.random() * ((original[1].flightBudget ?? 1) * 10 - 1)
          ) + 1;
      }

      const roas = original[1].flightSpend
        ? (revenue ?? 0) / original[1].flightSpend
        : 0;
      return (
        <BaseCell align="right">{roas && formatPercent(roas, 2)}</BaseCell>
      );
    },
  },
  {
    id: 'sales',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Sales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      const { campaignSalesAggregates } = useAdCampaignsDataContext();

      if (!original || !campaignSalesAggregates) {
        return <TableShimmeringDiv />;
      }

      // Find the campaign that contains this flight
      const campaignWithFlight = Object.values(campaignSalesAggregates).find(
        (campaign) =>
          campaign.flightsAggregate &&
          campaign.flightsAggregate[original[1].flightId]
      );

      const flightData =
        campaignWithFlight?.flightsAggregate?.[original[1].flightId];

      return <BaseCell align="right">{flightData?.saleCount}</BaseCell>;
    },
  },
  {
    id: 'budget',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Budget} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[1].flightBudget,
    cell: function Cell({ getValue, row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const currency = getUiCurrency(original[0].currencyCode || 'USD');
      const budget = getValue() as number | null;

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: budget,
            currencyCode: currency.code,
            currencyDecimalDigits: currency.dec,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'remaining',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Remaining} />
      </TableHeaderCellDiv>
    ),
    cell: function Cell({ row: { original } }) {
      const { getUiCurrency } = useLocalizationContext();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const currency = getUiCurrency(original[0].currencyCode || 'USD');
      // Calculate budget left
      const budget = original[1].flightBudget;
      const spend = original[1].flightSpend;

      const remaining = (budget ?? 0) - (spend ?? 0);

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: remaining,
            currencyCode: currency.code,
            currencyDecimalDigits: currency.dec,
          })}
        </BaseCell>
      );
    },
  },
  {
    id: 'target',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Target} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data[0].targetTicketClassIds,
    cell: function Cell({ row: { original } }) {
      const { venueMapInfo } = useEventMapContext();

      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      const ticketClassesById = useMemo(() => {
        if (!venueMapInfo) return {};

        return venueMapInfo.sections
          .flatMap((s) => s.rows.map((r) => r.tktClass))
          .reduce(
            (record, ticketClass) => {
              if (ticketClass?.id) {
                record[ticketClass.id] = ticketClass;
              }
              return record;
            },
            {} as Record<number, TicketClassInfo>
          );
      }, [venueMapInfo]);

      if (!original) {
        return <TableShimmeringDiv />;
      }

      // Display target information
      let target = 'All Zones';
      if (
        original[0].targetTicketClassIds?.length &&
        Object.keys(ticketClassesById).length > 0
      ) {
        const targetedClasses = original[0].targetTicketClassIds
          .map((id) => ticketClassesById[id]?.name)
          .filter(Boolean);

        if (targetedClasses.length > 0) {
          target = targetedClasses.join(', ');
        }
      }

      return <BaseCell align="left">{target}</BaseCell>;
    },
  },
  {
    id: 'actions',
    header: () => (
      <TableHeaderCellDiv align="center">
        <Content id={ContentId.Actions} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data,
    cell: function Cell({ row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      return (
        <BaseCell
          align="center"
          style={{ padding: vars.spacing.sm, paddingRight: vars.spacing.m }}
        >
          <Stack gap="m" alignItems="center" justifyContent="center">
            <BoostListingButton campaign={original[0]} iconOnly />
          </Stack>
        </BaseCell>
      );
    },
  },
];
